/**
 * Streaming utilities for StudioAI feature
 */

import { tracking } from '@/lib/features/tracking';
import {
    CHAT_STATES,
    DEFAULT_MESSAGES,
    DispatchType,
    ROLE_TYPES,
    STATUS_MESSAGES,
    STREAMING_EVENT_TYPES,
    TEMP_MESSAGE_IDS,
} from '../constants';
import type {
    AgentOptionsPayload,
    ChatMessage,
    ChatMessageContent,
    PlanSummaryPayload,
    StreamingEvent,
    StreamingEventHandlers,
    StreamingState,
    StudioAIAction,
} from '../types';

import {
    appendTextContent,
    appendThinkingContent,
    cloneMessageContent,
    createAIMessage,
    createPendingAiId,
    ensureContentArray,
    updateMessageById,
} from './message-utils';
import { applyReplacementsToChartSpec, applyWordReplacements } from './text-utils';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function isStillActiveConversation(handlers: StreamingEventHandlers): boolean {
    const live = handlers.currentConversationIdRef.current ?? null;
    const owned = handlers.streamingState.conversationId ?? null;
    return live === owned;
}

function bailOutOfStaleStream(handlers: StreamingEventHandlers): void {
    const { streamingState, timeoutId, resolve } = handlers;
    clearTimeout(timeoutId);
    if (!streamingState.isResolved) {
        streamingState.isResolved = true;
        resolve();
    }
}

/**
 * When a stream ends with nothing on screen, says so instead of leaving a blank answer
 * (BT-2067): the idle timeout, a connection that closes without `done`, and an empty `done`
 * all come here. Planner runs are left alone, because their report arrives on its own page.
 */
export function showNoAnswerNotice(handlers: StreamingEventHandlers): void {
    const { dispatch, streamingState } = handlers;
    if (!isStillActiveConversation(handlers)) return;
    if (streamingState.isPlannerMode || streamingState.hasError) return;

    const existing = streamingState.currentAiMessage
        ? (ensureContentArray(streamingState.currentAiMessage.content) as ChatMessageContent[])
        : [];
    const hasVisibleAnswer = existing.some(
        (item) =>
            item.type !== STREAMING_EVENT_TYPES.THINKING &&
            (item.type !== STREAMING_EVENT_TYPES.TEXT || !!item.text?.trim()),
    );
    if (hasVisibleAnswer) return;

    const content: ChatMessageContent[] = [
        ...existing,
        { type: STREAMING_EVENT_TYPES.TEXT, text: DEFAULT_MESSAGES.NO_ANSWER },
    ];
    const aiMessageId = streamingState.currentAiMessage?.id ?? null;
    dispatch({
        type: DispatchType.SetMessages,
        payload: (prevMessages: ChatMessage[]) => {
            if (aiMessageId) {
                return updateMessageById(prevMessages, aiMessageId, {
                    content,
                    isStreaming: false,
                });
            }
            const notice = createAIMessage(createPendingAiId(streamingState.conversationId));
            notice.content = content;
            notice.isStreaming = false;
            return [...prevMessages, notice];
        },
    });
}

/**
 * Convert result_set data to markdown table format
 */
export function convertResultSetToMarkdown(resultSet: {
    data: string[][];
    resultSetMetaData?: {
        rowType?: Array<{ name: string; type?: string }>;
    };
}): string {
    if (!resultSet.data || resultSet.data.length === 0) {
        return '';
    }

    const { data, resultSetMetaData } = resultSet;
    const columnNames =
        resultSetMetaData?.rowType?.map((col) => applyWordReplacements(col.name)) ||
        data[0].map((_, i) => `Column ${i + 1}`);

    // Create header row
    const headerRow = `| ${columnNames.join(' | ')} |`;
    const separatorRow = `| ${columnNames.map(() => '---').join(' | ')} |`;

    // Create data rows with word replacements applied
    const dataRows = data
        .map((row) => `| ${row.map((cell) => applyWordReplacements(cell)).join(' | ')} |`)
        .join('\n');

    return `${headerRow}\n${separatorRow}\n${dataRows}`;
}

// ============================================================================
// STREAMING EVENT HANDLERS
// ============================================================================

/**
 * Handle metadata streaming events
 */
export function handleMetadataEvent(data: StreamingEvent, handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState, state } = handlers;

    // Update conversation info if metadata is received
    streamingState.conversationId = data.conversationId || streamingState.conversationId;
    streamingState.isNewConversation = data.isNewConversation || false;

    // STEP 1: Handle usage metadata (token counts)
    if (data.usage && streamingState.currentAiMessage) {
        const existingMetadata = streamingState.currentAiMessage.metadata || {};
        streamingState.currentAiMessage.metadata = {
            ...existingMetadata,
            usage: data.usage,
        };

        // Update the message in state with the usage metadata
        dispatch({
            type: DispatchType.UpdateMessage,
            payload: {
                messageId: streamingState.currentAiMessage.id,
                updates: {
                    metadata: streamingState.currentAiMessage.metadata,
                },
            },
        });
        // NOTE: No return here - continue to process message_id if present
    }

    // STEP 2: Handle message_id metadata (message tracking)
    let messageId: string | null = null;
    let role = 'unknown';

    // First, check if messageId was already extracted by transformEventData
    if (data.messageId) {
        messageId = data.messageId.toString();

        // Parse the content to get the role
        try {
            const metadataContent = JSON.parse(data.content || '{}');
            role = metadataContent.role || 'unknown';
        } catch (error) {
            console.warn(
                'Failed to parse metadata content for role:',
                data.content,
                'error:',
                error,
            );
        }
    }
    // Fallback: For metadata events, parse the raw content to extract message_id
    else if (data.content && typeof data.content === 'string') {
        try {
            // Use a regex to extract the message_id as a string to preserve precision
            const messageIdMatch = data.content.match(/"message_id"[:\s]*"?(\d+)"?/);
            const roleMatch = data.content.match(/"role"[:\s]*"([^"]+)"/);

            messageId = messageIdMatch?.[1] || null; // Keep as string to preserve precision
            role = roleMatch?.[1] || 'unknown';
        } catch (error) {
            console.warn('Failed to parse raw metadata content:', data.content, 'error:', error);
        }
    }

    // Process the message_id if found
    if (messageId !== null) {
        // Update the message ID in the appropriate message
        if (role === ROLE_TYPES.USER) {
            // Find the user message with pending ID
            const messages = (state as { messages?: ChatMessage[] }).messages || [];
            const userMessage = messages.find(
                (msg: ChatMessage) =>
                    msg.role === ROLE_TYPES.USER && msg.id === TEMP_MESSAGE_IDS.PENDING_USER,
            );
            if (userMessage) {
                dispatch({
                    type: DispatchType.UpdateMessage,
                    payload: {
                        messageId: TEMP_MESSAGE_IDS.PENDING_USER,
                        updates: { id: messageId },
                    },
                });
            }
        } else if (role === ROLE_TYPES.ASSISTANT) {
            const currentPendingId = streamingState.currentAiMessage?.id;
            if (streamingState.currentAiMessage && currentPendingId) {
                dispatch({
                    type: DispatchType.UpdateMessage,
                    payload: {
                        messageId: currentPendingId,
                        updates: { id: messageId },
                    },
                });
                streamingState.currentAiMessage.id = messageId;
                streamingState.aiMessageId = messageId;
            }
        }

        // Only store the assistant's message ID as parent message ID for the next message
        if (role === ROLE_TYPES.ASSISTANT) {
            // Keep as string to preserve precision
            dispatch({ type: DispatchType.SetParentMessageId, payload: messageId as string });
        }
    }
}

/**
 * Handle status streaming events
 */
export function handleStatusEvent(data: StreamingEvent, handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;

    // Don't process status events if streaming is already resolved (DONE event received)
    if (streamingState.isResolved) {
        return;
    }

    // Handle status messages - show as current status with icon
    const statusIcon = data.messageId || 'lucide:loader-2'; // Use messageId for icon

    if (data.content) {
        dispatch({
            type: DispatchType.SetCurrentStatus,
            payload: `${data.content}|${statusIcon}`,
        });
    }
}

/**
 * Handle delta content streaming events
 */
export function handleDeltaContentEvent(
    data: StreamingEvent,
    handlers: StreamingEventHandlers,
): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;
    const { currentAiMessage } = streamingState;

    if (!currentAiMessage) return;

    // Ensure status is visible when streaming starts
    if (!streamingState.hasStartedStreaming) {
        streamingState.hasStartedStreaming = true;
        dispatch({
            type: DispatchType.SetCurrentStatus,
            payload: `${STATUS_MESSAGES.PROCESSING.message}|${STATUS_MESSAGES.PROCESSING.icon}`,
        });
    }

    try {
        const contentArray = JSON.parse(data.content || '[]') as Array<Record<string, unknown>>;

        // Process each content item in the delta
        contentArray.forEach((contentItem) => {
            const { type } = contentItem;

            if (type === STREAMING_EVENT_TYPES.TEXT && contentItem.text) {
                // Append text immediately with word replacements
                currentAiMessage.content = ensureContentArray(currentAiMessage.content);
                currentAiMessage.content = appendTextContent(
                    currentAiMessage.content,
                    applyWordReplacements(contentItem.text as string),
                );
            } else if (type === STREAMING_EVENT_TYPES.THINKING) {
                // Cortex emits thinking as a `data:`-only envelope:
                //   {"delta":{"content":[{"type":"thinking","thinking":{"text":"..."}}]}}
                // Older planner streams used `item.text` directly, so
                // accept either shape.
                let thinkingText = '';
                if (contentItem.thinking && typeof contentItem.thinking === 'object') {
                    const thinkingObj = contentItem.thinking as Record<string, unknown>;
                    if (typeof thinkingObj.text === 'string') {
                        thinkingText = thinkingObj.text;
                    }
                } else if (typeof contentItem.text === 'string') {
                    thinkingText = contentItem.text;
                }
                if (thinkingText) {
                    currentAiMessage.content = ensureContentArray(currentAiMessage.content);
                    currentAiMessage.content = appendThinkingContent(
                        currentAiMessage.content,
                        applyWordReplacements(thinkingText),
                    );
                }
            } else if (type === STREAMING_EVENT_TYPES.CHART && contentItem.chart) {
                // Append chart immediately with word replacements applied to chart_spec
                currentAiMessage.content = ensureContentArray(currentAiMessage.content);

                const chartData = contentItem.chart as {
                    chart_spec?: string;
                    title?: string;
                };
                if (chartData.chart_spec) {
                    currentAiMessage.content = [
                        ...currentAiMessage.content,
                        {
                            type: STREAMING_EVENT_TYPES.CHART,
                            chart: {
                                chart_spec: applyReplacementsToChartSpec(chartData.chart_spec),
                                title: applyWordReplacements(
                                    chartData.title || 'Data Visualization',
                                ),
                            },
                        },
                    ];
                }
            } else if (type === STREAMING_EVENT_TYPES.TABLE && contentItem.table) {
                // Append table immediately with word replacements
                currentAiMessage.content = ensureContentArray(currentAiMessage.content);

                const tableData = contentItem.table as {
                    markdown?: string;
                    data?: unknown;
                };
                if (tableData.markdown) {
                    currentAiMessage.content = [
                        ...currentAiMessage.content,
                        {
                            type: STREAMING_EVENT_TYPES.TABLE,
                            table: {
                                markdown: applyWordReplacements(tableData.markdown),
                                isToolResult: false,
                                rawData: tableData.data as Record<string, unknown>,
                            },
                        },
                    ];
                }
            }
        });

        const aiMessageId = currentAiMessage.id;
        dispatch({
            type: DispatchType.SetMessages,
            payload: (prevMessages: ChatMessage[]) => {
                return updateMessageById(prevMessages, aiMessageId, {
                    content: cloneMessageContent(currentAiMessage.content as ChatMessageContent[]),
                    timestamp: new Date(),
                });
            },
        });
    } catch (error) {
        tracking.errorWithSource(error as Error, 'StreamingUtils.ts', {
            content: data.content,
        });
    }
}

/**
 * Handle structured SSE events (currently `plan_summary`) by appending
 * a typed content item to the current AI message.
 */
export function handleStructuredContentEvent(
    data: StreamingEvent,
    contentType: ChatMessageContent['type'],
    payloadKey: 'plan_summary' | 'video_progress' | 'video_themes' | 'clip',
    handlers: StreamingEventHandlers,
): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;
    const { currentAiMessage } = streamingState;

    if (!currentAiMessage) return;

    if (!streamingState.hasStartedStreaming) {
        streamingState.hasStartedStreaming = true;
        dispatch({
            type: DispatchType.SetCurrentStatus,
            payload: `${STATUS_MESSAGES.PROCESSING.message}|${STATUS_MESSAGES.PROCESSING.icon}`,
        });
    }

    try {
        const payload = data.content ? JSON.parse(data.content) : {};

        currentAiMessage.content = ensureContentArray(currentAiMessage.content);
        currentAiMessage.content = [
            ...currentAiMessage.content,
            {
                type: contentType,
                [payloadKey]: payload,
            } as ChatMessageContent,
        ];

        const aiMessageId = currentAiMessage.id;
        dispatch({
            type: DispatchType.SetMessages,
            payload: (prevMessages: ChatMessage[]) => {
                return updateMessageById(prevMessages, aiMessageId, {
                    content: cloneMessageContent(currentAiMessage.content as ChatMessageContent[]),
                    timestamp: new Date(),
                });
            },
        });
    } catch (error) {
        tracking.errorWithSource(error as Error, 'StreamingUtils.ts', {
            content: data.content,
        });
    }
}

/**
 * Dispatch a plan_progress snapshot into reducer state. This drives the
 * live plan sidebar and is intentionally disjoint from message content:
 * plan_progress is a latest-wins snapshot stored in its own reducer slot
 * (and its own `studioai_history.plan_progress` DB column), not an
 * append-only list item, so the sidebar is always a single source of
 * truth regardless of how many turns have streamed it.
 */
export function handlePlanProgressEvent(
    data: StreamingEvent,
    handlers: StreamingEventHandlers,
): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch } = handlers;
    if (!data.content) return;

    try {
        const payload = JSON.parse(data.content) as import('../types').PlanProgressPayload;
        dispatch({
            type: DispatchType.SetPlanProgress,
            payload,
        });
    } catch (error) {
        tracking.errorWithSource(error as Error, 'StreamingUtils.ts', {
            content: data.content,
        });
    }
}

/**
 * Append agent_options as a content item on the current AI message so it
 * persists in conversation history and can be restored on page reload.
 */
export function appendAgentOptionsContent(
    payload: import('../types').AgentOptionsPayload,
    handlers: StreamingEventHandlers,
): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;
    const { currentAiMessage } = streamingState;

    if (!currentAiMessage) return;

    currentAiMessage.content = ensureContentArray(currentAiMessage.content);
    currentAiMessage.content = [
        ...currentAiMessage.content,
        {
            type: STREAMING_EVENT_TYPES.AGENT_OPTIONS,
            agent_options: payload,
        } as ChatMessageContent,
    ];

    const aiMessageId = currentAiMessage.id;
    dispatch({
        type: DispatchType.SetMessages,
        payload: (prevMessages: ChatMessage[]) => {
            return updateMessageById(prevMessages, aiMessageId, {
                content: cloneMessageContent(currentAiMessage.content as ChatMessageContent[]),
                timestamp: new Date(),
            });
        },
    });
}

/**
 * Handle content array streaming events (final structured content)
 */
export function handleContentArrayEvent(
    data: StreamingEvent,
    handlers: StreamingEventHandlers,
): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, queryClient, streamingState } = handlers;
    const { currentAiMessage, conversationId, hasError, hasSaved } = streamingState;

    if (!currentAiMessage) return;

    try {
        const contentArray = JSON.parse(data.content || '[]') as Array<{
            type: string;
            content?: string;
            text?: string;
            thinking?: string;
            tool_use?: Record<string, unknown>;
            tool_result?: Record<string, unknown>;
            chart?: {
                chart_spec: string;
                title?: string;
            };
            table?: {
                markdown: string;
                data?: unknown;
            };
            plan_summary?: Record<string, unknown>;
            agent_options?: Record<string, unknown>;
            video_progress?: ChatMessageContent['video_progress'];
            video_themes?: ChatMessageContent['video_themes'];
            clip?: ChatMessageContent['clip'];
        }>;

        const newContent: ChatMessageContent[] = [];

        contentArray.forEach((contentItem) => {
            if (contentItem.type === STREAMING_EVENT_TYPES.THINKING && contentItem.content) {
                // Add thinking content with word replacements
                newContent.push({
                    type: STREAMING_EVENT_TYPES.THINKING,
                    text: applyWordReplacements(contentItem.content),
                });
            } else if (
                contentItem.type === STREAMING_EVENT_TYPES.TEXT ||
                contentItem.type === 'text'
            ) {
                // Add the final text content with word replacements
                let textContent = '';
                if (typeof contentItem.content === 'string') {
                    textContent = contentItem.content;
                } else if (typeof contentItem.text === 'string') {
                    textContent = contentItem.text;
                }

                if (textContent && textContent.trim() !== '') {
                    newContent.push({
                        type: STREAMING_EVENT_TYPES.TEXT,
                        text: applyWordReplacements(textContent),
                    });
                }
            } else if (contentItem.type === STREAMING_EVENT_TYPES.CHART && contentItem.chart) {
                // Add chart content with word replacements applied
                newContent.push({
                    type: STREAMING_EVENT_TYPES.CHART,
                    chart: {
                        chart_spec: applyReplacementsToChartSpec(
                            contentItem.chart.chart_spec as string,
                        ),
                        title: applyWordReplacements(
                            (contentItem.chart.title as string) || 'Data Visualization',
                        ),
                    },
                });
            } else if (contentItem.type === STREAMING_EVENT_TYPES.TABLE && contentItem.table) {
                // Add table content with word replacements
                const tableData = contentItem.table as Record<string, unknown>;
                let markdown = '';

                // Check if we have a result_set structure (new format)
                if (tableData.result_set && typeof tableData.result_set === 'object') {
                    const resultSet = tableData.result_set as {
                        data: string[][];
                        resultSetMetaData?: {
                            rowType?: Array<{ name: string; type?: string }>;
                        };
                    };
                    markdown = convertResultSetToMarkdown(resultSet);
                } else if (tableData.markdown && typeof tableData.markdown === 'string') {
                    // Existing markdown format - apply word replacements
                    markdown = applyWordReplacements(tableData.markdown);
                }

                if (markdown) {
                    newContent.push({
                        type: STREAMING_EVENT_TYPES.TABLE,
                        table: {
                            markdown,
                            isToolResult: false,
                            rawData: (tableData.result_set || tableData.data || undefined) as
                                | Record<string, unknown>
                                | string[][]
                                | undefined,
                            title: (tableData.title as string) || undefined,
                            query_id: (tableData.query_id as string) || undefined,
                            tool_use_id: (tableData.tool_use_id as string) || undefined,
                        },
                    });
                }
            } else if (
                contentItem.type === STREAMING_EVENT_TYPES.TOOL_USE &&
                contentItem.tool_use
            ) {
                // Add tool use content
                newContent.push({
                    type: STREAMING_EVENT_TYPES.TOOL_USE,
                    tool_use: {
                        name: (contentItem.tool_use.name as string) || 'unknown',
                        input: (contentItem.tool_use.input as Record<string, unknown>) || {},
                    },
                });
            } else if (
                (contentItem.type === 'tool_result' ||
                    contentItem.type === STREAMING_EVENT_TYPES.TOOL_RESULT) &&
                contentItem.tool_result
            ) {
                const toolResultData = (contentItem.tool_result ||
                    contentItem.tool_result) as Record<string, unknown>;
                newContent.push({
                    type: STREAMING_EVENT_TYPES.TOOL_RESULT,
                    tool_result: {
                        name: (toolResultData.name as string) || 'unknown',
                        content:
                            (toolResultData.content as Array<{
                                type: string;
                                json?: Record<string, unknown>;
                            }>) || [],
                    },
                });
            } else if (
                contentItem.type === STREAMING_EVENT_TYPES.PLAN_SUMMARY &&
                contentItem.plan_summary
            ) {
                newContent.push({
                    type: STREAMING_EVENT_TYPES.PLAN_SUMMARY,
                    plan_summary: contentItem.plan_summary as unknown as PlanSummaryPayload,
                });
            } else if (
                contentItem.type === STREAMING_EVENT_TYPES.AGENT_OPTIONS &&
                contentItem.agent_options
            ) {
                newContent.push({
                    type: STREAMING_EVENT_TYPES.AGENT_OPTIONS,
                    agent_options: contentItem.agent_options as unknown as AgentOptionsPayload,
                });
            } else if (
                contentItem.type === STREAMING_EVENT_TYPES.VIDEO_PROGRESS &&
                contentItem.video_progress
            ) {
                newContent.push({
                    type: STREAMING_EVENT_TYPES.VIDEO_PROGRESS,
                    video_progress: contentItem.video_progress,
                });
            } else if (
                contentItem.type === STREAMING_EVENT_TYPES.VIDEO_THEMES &&
                contentItem.video_themes
            ) {
                newContent.push({
                    type: STREAMING_EVENT_TYPES.VIDEO_THEMES,
                    video_themes: contentItem.video_themes,
                });
            } else if (contentItem.type === STREAMING_EVENT_TYPES.CLIP && contentItem.clip) {
                newContent.push({ type: STREAMING_EVENT_TYPES.CLIP, clip: contentItem.clip });
            }
        });

        // Update message with final structured content
        currentAiMessage.content = newContent;
        currentAiMessage.isStreaming = false;

        // Also store usage metadata if present in the event
        if (data.usage) {
            const existingMetadata = currentAiMessage.metadata || {};
            currentAiMessage.metadata = {
                ...existingMetadata,
                usage: data.usage,
            };
        }

        const aiMessageId = currentAiMessage.id;
        dispatch({
            type: DispatchType.SetMessages,
            payload: (prevMessages: ChatMessage[]) => {
                const updates: Partial<ChatMessage> = {
                    content: newContent,
                    isStreaming: false,
                    timestamp: new Date(),
                    _animate: true,
                };

                // Include metadata in the update if we have usage data
                if (currentAiMessage.metadata) {
                    updates.metadata = currentAiMessage.metadata;
                }

                const newMessages = updateMessageById(prevMessages, aiMessageId, updates);

                // Backend now handles saving via appendToThreadHistory - no need to save from frontend
                // Just invalidate queries to refresh the conversations list
                if (conversationId && newMessages.length > 0 && !hasError && !hasSaved) {
                    streamingState.hasSaved = true;
                    queryClient.invalidateQueries({
                        queryKey: ['studio-ai', 'conversations'],
                    });
                }

                return newMessages;
            },
        });

        // Mark that final content has been processed
        streamingState.finalContentProcessed = true;
    } catch (error) {
        console.error('Error parsing content array:', error);
    }
}

/**
 * Handle chart streaming events
 */
export function handleChartEvent(data: StreamingEvent, handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;
    const { currentAiMessage } = streamingState;

    if (!currentAiMessage || !data.chart) return;

    // Ensure status is visible when streaming starts
    if (!streamingState.hasStartedStreaming) {
        streamingState.hasStartedStreaming = true;
        dispatch({
            type: DispatchType.SetCurrentStatus,
            payload: `${STATUS_MESSAGES.PROCESSING.message}|${STATUS_MESSAGES.PROCESSING.icon}`,
        });
    }

    // Add chart content immediately with word replacements applied
    currentAiMessage.content = ensureContentArray(currentAiMessage.content);

    if (data.chart.chart_spec) {
        currentAiMessage.content = [
            ...currentAiMessage.content,
            {
                type: STREAMING_EVENT_TYPES.CHART,
                chart: {
                    chart_spec: applyReplacementsToChartSpec(data.chart.chart_spec),
                    title: applyWordReplacements(data.chart.title || 'Data Visualization'),
                },
            },
        ];

        const aiMessageId = currentAiMessage.id;
        dispatch({
            type: DispatchType.SetMessages,
            payload: (prevMessages: ChatMessage[]) => {
                return updateMessageById(prevMessages, aiMessageId, {
                    content: cloneMessageContent(currentAiMessage.content as ChatMessageContent[]),
                    timestamp: new Date(),
                });
            },
        });
    }
}

/**
 * Handle text streaming events (thinking content during streaming)
 *
 * This shows thinking progress to the user while streaming.
 * Note: The final authoritative content comes from handleContentArrayEvent,
 * so this handler just provides visual feedback during the streaming process.
 */
export function handleTextEvent(data: StreamingEvent, handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;
    const { currentAiMessage } = streamingState;

    if (!currentAiMessage) {
        return;
    }

    // Ensure status is visible when streaming starts
    if (!streamingState.hasStartedStreaming) {
        streamingState.hasStartedStreaming = true;
        dispatch({
            type: DispatchType.SetCurrentStatus,
            payload: `${STATUS_MESSAGES.PROCESSING.message}|${STATUS_MESSAGES.PROCESSING.icon}`,
        });
    }

    const content = data.content || '';

    // Skip empty content
    if (!content || content.trim() === '') {
        return;
    }

    // Append thinking content with word replacements
    // Note: handleContentArrayEvent will replace this with the final structured content
    currentAiMessage.content = ensureContentArray(currentAiMessage.content);
    currentAiMessage.content = appendThinkingContent(
        currentAiMessage.content,
        applyWordReplacements(content),
    );

    const aiMessageId = currentAiMessage.id;
    dispatch({
        type: DispatchType.SetMessages,
        payload: (prevMessages: ChatMessage[]) => {
            return updateMessageById(prevMessages, aiMessageId, {
                content: cloneMessageContent(currentAiMessage.content as ChatMessageContent[]),
                timestamp: new Date(),
            });
        },
    });
}

/**
 * Handle done streaming events
 *
 * Called when the backend sends a 'done' event to signal completion.
 * Note: Backend now handles saving via appendToThreadHistory,
 * so we just mark completion and clean up.
 */
export function handleDoneEvent(handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState, timeoutId, resolve } = handlers;
    const { currentAiMessage } = streamingState;

    if (currentAiMessage) {
        const aiMessageId = currentAiMessage.id;

        if (!streamingState.finalContentProcessed) {
            // CONTENT_ARRAY was never received — commit whatever accumulated via delta events.
            // This happens when extended thinking tokens arrive but no CONTENT_ARRAY follows.
            const accumulatedContent = ensureContentArray(
                currentAiMessage.content,
            ) as ChatMessageContent[];
            dispatch({
                type: DispatchType.SetMessages,
                payload: (prevMessages: ChatMessage[]) => {
                    return updateMessageById(prevMessages, aiMessageId, {
                        content: cloneMessageContent(accumulatedContent),
                        isStreaming: false,
                        timestamp: new Date(),
                        _animate: true,
                    });
                },
            });
        } else {
            dispatch({
                type: DispatchType.SetMessages,
                payload: (prevMessages: ChatMessage[]) => {
                    return updateMessageById(prevMessages, aiMessageId, {
                        isStreaming: false,
                    });
                },
            });
        }
    }

    showNoAnswerNotice(handlers);
    dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });
    clearTimeout(timeoutId);

    // Mark as resolved to prevent handleStreamingComplete from running
    if (!streamingState.isResolved) {
        streamingState.isResolved = true;
        resolve();
    }
}

/**
 * Handle error streaming events
 *
 * Creates or updates the AI message with error information and saves the conversation.
 * Unlike success cases, we need frontend saving for errors since backend may not persist them.
 */
export function handleErrorEvent(data: StreamingEvent, handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, queryClient, streamingState, timeoutId } = handlers;

    streamingState.hasError = true;

    const errorMessage = data.message || data.content || 'Unknown error';
    tracking.error(new Error('Streaming error received'), {
        errorMessage,
        conversationId: streamingState.conversationId,
        messageId: streamingState.currentAiMessage?.id,
    });

    // Build error content
    const errorTextContent: ChatMessageContent = {
        type: STREAMING_EVENT_TYPES.TEXT,
        text: DEFAULT_MESSAGES.ERROR,
    };
    const errorThinkingContent: ChatMessageContent = {
        type: STREAMING_EVENT_TYPES.THINKING,
        text: `Error Details:\n\nCode: ${data.code || 'Unknown'}\nMessage: ${errorMessage}\nRequest ID: ${data.request_id || 'N/A'}`,
    };

    // Determine the content to use
    let errorContent: ChatMessageContent[];
    if (streamingState.currentAiMessage) {
        const existingContent = ensureContentArray(streamingState.currentAiMessage.content);
        const hasSubstantialText = existingContent.some(
            (item) =>
                item.type === STREAMING_EVENT_TYPES.TEXT &&
                item.text &&
                item.text.trim().length > 10,
        );

        if (hasSubstantialText) {
            // Keep existing content, just add error details
            errorContent = [...existingContent, errorThinkingContent];
        } else {
            // Replace with error content
            const nonTextContent = existingContent.filter(
                (c) => c.type !== STREAMING_EVENT_TYPES.TEXT,
            );
            errorContent = [...nonTextContent, errorTextContent, errorThinkingContent];
        }
    } else {
        errorContent = [errorTextContent, errorThinkingContent];
    }

    const aiMessageIdForError = streamingState.currentAiMessage?.id ?? null;
    dispatch({
        type: DispatchType.SetMessages,
        payload: (prevMessages: ChatMessage[]) => {
            let newMessages: ChatMessage[];

            if (aiMessageIdForError) {
                newMessages = updateMessageById(prevMessages, aiMessageIdForError, {
                    content: errorContent,
                    isStreaming: false,
                });
            } else {
                const errorAIMessage = createAIMessage(
                    createPendingAiId(streamingState.conversationId),
                );
                errorAIMessage.content = errorContent;
                errorAIMessage.isStreaming = false;
                newMessages = [...prevMessages, errorAIMessage];
            }

            return newMessages;
        },
    });

    // Set error state and cleanup
    dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.ERROR });
    dispatch({ type: DispatchType.SetCurrentStatus, payload: null });
    dispatch({ type: DispatchType.SetLoading, payload: false });
    clearTimeout(timeoutId);

    if (!streamingState.isResolved) {
        streamingState.isResolved = true;
        handlers.resolve();
    }
}

/**
 * Handle completion of streaming (called when SSE connection closes)
 *
 * This is a fallback handler called when the SSE stream ends.
 * Usually handleDoneEvent will have already resolved by this point.
 * Note: Backend handles saving via appendToThreadHistory.
 */
export function handleStreamingComplete(handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState, timeoutId, resolve } = handlers;
    const { currentAiMessage, isResolved } = streamingState;

    // If already resolved by handleDoneEvent, don't do anything
    if (isResolved) {
        return;
    }

    if (currentAiMessage) {
        const aiMessageId = currentAiMessage.id;
        dispatch({
            type: DispatchType.SetMessages,
            payload: (prevMessages: ChatMessage[]) => {
                return updateMessageById(prevMessages, aiMessageId, {
                    isStreaming: false,
                });
            },
        });
    }

    showNoAnswerNotice(handlers);
    clearTimeout(timeoutId);

    dispatch({ type: DispatchType.SetChatState, payload: CHAT_STATES.IDLE });
    dispatch({ type: DispatchType.ClearError });

    if (!streamingState.isResolved) {
        streamingState.isResolved = true;
        resolve();
    }
}

// ============================================================================
// STREAMING ORCHESTRATION
// ============================================================================

/**
 * Create initial streaming state
 */
export function createStreamingState(): StreamingState {
    return {
        aiMessageId: null,
        conversationId: null,
        isNewConversation: false,
        currentAiMessage: null,
        isResolved: false,
        hasError: false,
        hasSaved: false,
        finalContentProcessed: false,
        hasStartedStreaming: false,
    };
}

/**
 * Initialize AI message for streaming. Allocates a per-mutation pending
 * id scoped by conversationId so two concurrent streams (or one
 * cancelled, one new) cannot collide on a shared constant.
 */
export function initializeAIMessage(_data: StreamingEvent, handlers: StreamingEventHandlers): void {
    if (!isStillActiveConversation(handlers)) {
        bailOutOfStaleStream(handlers);
        return;
    }

    const { dispatch, streamingState } = handlers;

    if (!streamingState.currentAiMessage) {
        const pendingId = createPendingAiId(streamingState.conversationId);
        streamingState.aiMessageId = pendingId;
        streamingState.currentAiMessage = createAIMessage(pendingId);
        dispatch({ type: DispatchType.AddMessage, payload: streamingState.currentAiMessage });
    }
}
